import type { SubmitEvent } from 'react';

import { t } from '@web/i18n';
import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@web/ui/core';

/** Supported airline / GDS processors. The value is the integrator's `processorType`. */
const PROCESSOR_TYPES = ['azul', 'latam', 'gol', 'sabre'] as const;
type ProcessorType = (typeof PROCESSOR_TYPES)[number];

const PROCESSOR_LABEL: Record<ProcessorType, () => string> = {
  azul: () => t('scheduleChanges.processor.azul'),
  latam: () => t('scheduleChanges.processor.latam'),
  gol: () => t('scheduleChanges.processor.gol'),
  sabre: () => t('scheduleChanges.processor.sabre'),
};

interface UploadFormProps {
  processorType: string;
  onProcessorTypeChange: (value: string) => void;
  file: File | null;
  onFileChange: (file: File | null) => void;
  onSubmit: () => void;
  isPending: boolean;
}

/**
 * Upload form: processor-type `Select` + native file input + submit. The page
 * owns the form state; this component is presentational. Submit is disabled
 * until both a type and a file are present, or while a request is in flight.
 */
export function UploadForm({
  processorType,
  onProcessorTypeChange,
  file,
  onFileChange,
  onSubmit,
  isPending,
}: Readonly<UploadFormProps>): React.JSX.Element {
  const canSubmit = processorType !== '' && file !== null && !isPending;

  function submit(e: SubmitEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (canSubmit) onSubmit();
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="schedule-change-processor">
          {t('scheduleChanges.form.processorType.label')}
        </Label>
        <Select value={processorType} onValueChange={onProcessorTypeChange} disabled={isPending}>
          <SelectTrigger id="schedule-change-processor" className="max-w-xs">
            <SelectValue placeholder={t('scheduleChanges.form.processorType.placeholder')} />
          </SelectTrigger>
          {/* item-aligned (not popper) so options render without FloatingUI
              measurement — keeps the picker driveable in jsdom tests. */}
          <SelectContent position="item-aligned">
            {PROCESSOR_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {PROCESSOR_LABEL[type]()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="schedule-change-file">{t('scheduleChanges.form.file.label')}</Label>
        <input
          id="schedule-change-file"
          type="file"
          disabled={isPending}
          onChange={(e) => {
            onFileChange(e.target.files?.[0] ?? null);
          }}
          className="max-w-md text-meta text-foreground file:mr-3 file:rounded-md file:border file:border-input file:bg-card file:px-3 file:py-1.5 file:text-meta file:text-foreground hover:file:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      <div>
        <Button variant="primary" type="submit" disabled={!canSubmit}>
          {isPending ? t('scheduleChanges.form.submitting') : t('scheduleChanges.form.submit')}
        </Button>
      </div>
    </form>
  );
}
